import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { isSubcontractor } from './utils/roles';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import SupportPortal from './components/SupportPortal/SupportPortal';
import FormIndex from './components/HR/Forms/FormIndex';
import UniformFormPage from './components/HR/Forms/forms/UniformFormPage';
import DirectDepositFormPage from './components/HR/Forms/forms/DirectDepositFormPage';
import CellPhoneFormPage from './components/HR/Forms/forms/CellPhoneFormPage';
import DrugAlcoholPolicyFormPage from './components/HR/Forms/forms/DrugAlcoholPolicyFormPage';
import TruckSchedulePrint from './components/Schedule/TruckSchedulePrint';
import JobStatusPage from './components/Public/JobStatusPage';
import SignDocumentPage from './components/Public/SignDocumentPage';
import CrewToday from './components/Crew/CrewToday';
import DemoCrewLogin from './components/Crew/DemoCrewLogin';
import DemoLauncher from './demo/DemoLauncher';
import SmtpHealthGate from './components/Admin/SmtpHealthGate';
import AssistantWidget from './components/Assistant/AssistantWidget';
import UpdateAvailablePrompt from './components/Common/UpdateAvailablePrompt';
import './i18n'; // Initialize i18n
import './App.css';

const SHOW_SMTP_HEALTH_GATE = process.env.REACT_APP_SHOW_SMTP_HEALTH_GATE === 'true';

// Custom theme for the landscaping business
const theme = createTheme({
  palette: {
    primary: {
      main: '#000062',
      light: '#3d3c8a',
      dark: '#000043'
    },
    secondary: {
      main: '#de6127',
      light: '#e37d4c',
      dark: '#b54a1a'
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff'
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"BEBAS NEUE", "Arial Black", sans-serif',
      fontWeight: 400,
      fontSize: '2.5rem',
      letterSpacing: '0.02em',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontFamily: '"BEBAS NEUE", "Arial Black", sans-serif',
      fontWeight: 400,
      fontSize: '2rem',
      letterSpacing: '0.02em',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontFamily: '"BEBAS NEUE", "Arial Black", sans-serif',
      fontWeight: 400,
      fontSize: '1.75rem',
      letterSpacing: '0.02em',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontFamily: '"BEBAS NEUE", "Arial Black", sans-serif',
      fontWeight: 400,
      fontSize: '1.5rem',
      letterSpacing: '0.02em',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontFamily: '"BEBAS NEUE", "Arial Black", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.02em'
    },
    h6: {
      fontFamily: '"BEBAS NEUE", "Arial Black", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.02em'
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            minHeight: '44px',
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '10px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          minWidth: '120px', // Ensure dropdowns are wide enough to show their content
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          minWidth: '120px', // Ensure form controls have minimum width
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          minWidth: '120px', // Ensure input bases have minimum width
        },
      },
    },
  },
});

// Returns the appropriate landing path for a signed-in user. Field crew (the
// current driver/passenger of an active truck, never admins) go to the minimal
// /crew mobile-first experience; everyone else sees the manager/admin
// /dashboard. is_field_crew is computed server-side at login + /profile
// (TKT-221 follow-up: the legacy 'crew' user_type was merged into 'employee').
const landingPathForUser = (user) => {
  if (user && (user.is_field_crew || user.user_type === 'crew_manager' || isSubcontractor(user))) return '/crew';
  return '/dashboard';
};

// Protected Route Component. TKT-461: subcontractors only have Crew Mode, so
// any other page sends them back to /crew.
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ bgcolor: 'background.default' }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isSubcontractor(user) && location.pathname !== '/crew') return <Navigate to="/crew" replace />;
  return children;
};

// Dashboard stays available after login. Field crew and crew managers still
// land on /crew from the login screen, and a dashboard button sends them back.
const DashboardRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: 'background.default' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // TKT-461: subcontractors have no dashboard, only Crew Mode.
  if (isSubcontractor(user)) return <Navigate to="/crew" replace />;
  return children;
};

// Public Route Component (redirects authenticated users to their landing page)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ bgcolor: 'background.default' }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return !isAuthenticated ? children : <Navigate to={landingPathForUser(user)} replace />;
};

// Main App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/dashboard/*" 
        element={
          <DashboardRoute>
            <Dashboard />
          </DashboardRoute>
        } 
      />

      <Route
        path="/demo/crew-login"
        element={
          <ProtectedRoute>
            <DemoCrewLogin />
          </ProtectedRoute>
        }
      />

      {/* Crew mode: minimal, mobile-first landing for field workers. */}
      <Route
        path="/crew"
        element={
          <ProtectedRoute>
            <CrewToday />
          </ProtectedRoute>
        }
      />
      <Route path="/support/*" element={<SupportPortal />} />

      {/* Public (no-auth) customer status page reached from the
          "Property Live Update" email link. */}
      <Route path="/public/job-status/:token" element={<JobStatusPage />} />
      {/* Public contract signing page reached from the e-sign email (TKT-401) */}
      <Route path="/sign/:token" element={<SignDocumentPage />} />
      
      {/* Employee Forms Routes - These are OUTSIDE Dashboard */}
      <Route 
        path="/forms" 
        element={
          <ProtectedRoute>
            <FormIndex />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/forms/3"
        element={
          <ProtectedRoute>
            <UniformFormPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/forms/9" 
        element={
          <ProtectedRoute>
            <DirectDepositFormPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/forms/8" 
        element={
          <ProtectedRoute>
            <CellPhoneFormPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/forms/11" 
        element={
          <ProtectedRoute>
            <DrugAlcoholPolicyFormPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/forms/:formId" 
        element={
          <ProtectedRoute>
            <FormIndex />
          </ProtectedRoute>
        } 
      />
      
      {/* Standalone printable per-truck daily schedule */}
      <Route
        path="/schedule/print"
        element={
          <ProtectedRoute>
            <TruckSchedulePrint />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      {/* Unknown crew-mode subpath -> back to /crew */}
      <Route path="/crew/*" element={<Navigate to="/crew" replace />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="App">
            <AppRoutes />
            <AssistantWidget />
            <UpdateAvailablePrompt />
            <DemoLauncher />
            {SHOW_SMTP_HEALTH_GATE && <SmtpHealthGate />}
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 